import { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Link,
    CircularProgress,
    IconButton,
    InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";

import CustomButton from "../../components/common/CustomButton";
import CustomInput from "../../components/common/CustomInput";
import CustomSnackbar from "../../components/common/CustomSnackbar";
import Cybermeru from "../../assets/images/merubot_logo.png";
import { authApi } from "../../api/authApi";
import LoginIllustration from "../../assets/images/login_illustration.png";
import { GoogleLogin } from "@react-oauth/google";
import { useBranding } from "../../hooks/useBranding";
import { emailRule, passwordRule } from "../../components/validator/commonRules";
import { useAuth } from "../../contexts/AuthContext";

const INITIAL_BLOCK_STATE = {
    isBlocked: false,
    timeRemaining: 0,
};

const Login = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { handleContextLogin } = useBranding();
    const { login_lock_until, setAuthData } = useAuth();

    const searchParams = new URLSearchParams(window.location.search);
    const emailFromUrl = searchParams.get("email") || "";

    const [password, setPassword] = useState("");
    const [errors, setErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loginBlock, setLoginBlock] = useState(INITIAL_BLOCK_STATE);
    const primary = theme.palette.primary.main;

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });

    /* ---------------- Helpers ---------------- */

    const getTenantSlug = () => {
        const hostname = window.location.hostname;
        const parts = hostname.split(".");
        if (hostname.includes("localhost")) return null;
        if (parts.length > 3) return parts[0];
        return null;
    };

    const getCentralUrl = (path) => {
        const hostname = window.location.hostname;
        const port = window.location.port;
        const protocol = window.location.protocol;
        const parts = hostname.split(".");

        let isSubdomain = false;
        if (hostname.includes("localhost")) {
            if (parts.length > 2) {
                isSubdomain = true;
            }
        } else {
            if (parts.length > 3) {
                isSubdomain = true;
            }
        }

        if (isSubdomain) {
            const centralHostname = parts.slice(1).join(".");
            const hostWithPort = port ? `${centralHostname}:${port}` : centralHostname;
            return `${protocol}//${hostWithPort}${path}`;
        }

        return path;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === "password") {
            setPassword(value?.trim());
            if (!value.trim()) {
                setErrors((prev) => ({ ...prev, password: "Required Field" }));
            } else if (value.trim().length > 20) {
                setErrors((prev) => ({ ...prev, password: "Password cannot exceed 20 characters" }));
            } else {
                setErrors((prev) => ({ ...prev, password: "" }));
            }
        }
    };

    const handleSnackbar = (message, severity = "info") =>
        setSnackbar({ open: true, message, severity });

    const formatTime = (seconds) =>
        `${Math.floor(seconds / 60)}:${(seconds % 60).toString().padStart(2, "0")}`;

    /* ---------------- Redirect if No Email ---------------- */
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const emailParam = params.get("email");
        if (!emailParam) {
            const centralUrl = getCentralUrl("/email/redirect");
            if (centralUrl.startsWith("http")) {
                window.location.replace(centralUrl);
            } else {
                navigate(centralUrl, { replace: true });
            }
        }
    }, [navigate]);

    /* ---------------- Check User Status on Mount ---------------- */
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const emailParam = params.get("email");
        if (!emailParam) return; // will be redirected above

        (async () => {
            try {
                const response = await authApi.checkUserStatus({ email: emailParam });
                const { status: httpStatus, data } = response || {};

                if (httpStatus === 0) return; // network error — let user try logging in anyway

                if (httpStatus !== 200) return; // email not found etc — login will handle it

                const userStatus = data?.status;
                const isPasswordChanged = data?.is_password_changed;

                if (userStatus === "inactive") {
                    handleSnackbar(
                        "Your account is not yet active. Please contact your administrator.",
                        "warning"
                    );
                    setTimeout(() => {
                        const centralUrl = getCentralUrl("/email/redirect");
                        if (centralUrl.startsWith("http")) {
                            window.location.replace(centralUrl);
                        } else {
                            navigate(centralUrl, { replace: true });
                        }
                    }, 3000);
                    return;
                }

                if (!isPasswordChanged) {
                    handleSnackbar(
                        "Your account setup is incomplete. Please generate your initial password using the link sent to your registration email.",
                        "warning"
                    );
                    setTimeout(() => {
                        const centralUrl = getCentralUrl("/email/redirect");
                        if (centralUrl.startsWith("http")) {
                            window.location.replace(centralUrl);
                        } else {
                            navigate(centralUrl, { replace: true });
                        }
                    }, 4000);
                }
            } catch {
                // silent — login itself will surface any real errors
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* ---------------- Restore Login Lock After Refresh ---------------- */

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const email = params.get("email");

        if (!email) return;

        const lockUntil = login_lock_until[email];

        if (!lockUntil) return;

        const remaining = Math.ceil(
            (Number(lockUntil) - Date.now()) / 1000
        );

        if (remaining > 0) {
            setLoginBlock({
                isBlocked: true,
                timeRemaining: remaining,
            });
        } else {
            const newLocks = { ...login_lock_until };
            delete newLocks[email];
            setAuthData({ login_lock_until: newLocks });
        }
    }, []);
    /* ---------------- Cooldown Timer ---------------- */

    useEffect(() => {
        if (!loginBlock.isBlocked) return;

        if (loginBlock.timeRemaining <= 0) {
            const params = new URLSearchParams(window.location.search);
            const email = params.get("email");

            if (email) {
                const newLocks = { ...login_lock_until };
                delete newLocks[email];
                setAuthData({ login_lock_until: newLocks });
            }

            setLoginBlock(INITIAL_BLOCK_STATE);
            return;
        }

        const timer = setTimeout(() => {
            setLoginBlock((prev) => ({
                ...prev,
                timeRemaining: prev.timeRemaining - 1,
            }));
        }, 1000);

        return () => clearTimeout(timer);
    }, [loginBlock]);

    /* ---------------- Auto Google Click ---------------- */

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const autoGoogle = params.get("google");

        if (
            autoGoogle === "1" &&
            window.location.hostname === "dev.merubot.com"
        ) {
            // Keep trying every 300ms until Google button renders,
            // stop automatically after 5 seconds as safety fallback
            const interval = setInterval(() => {
                const googleBtn = document.querySelector('div[role="button"]');
                if (googleBtn) {
                    googleBtn.click();
                    clearInterval(interval);
                }
            }, 300);

            setTimeout(() => clearInterval(interval), 5000);
        }
    }, []);

    /* ---------------- Login Handler ---------------- */

    const handleLogin = async (e) => {
        if (e) e.preventDefault();
        if (loading) return;

        const trimmedPassword = password.trim();
        setPassword(trimmedPassword); // Update state to match the trimmed/validated value

        if (!emailFromUrl || !trimmedPassword) {
            setErrors({ password: "Required Field" });
            if (!emailFromUrl) handleSnackbar("Email is required.", "warning");
            return;
        }

        let validatedEmail = emailFromUrl.trim();
        try {
            validatedEmail = emailRule("Email address", { required: true }).validateSync(validatedEmail);
        } catch (err) {
            handleSnackbar(err.message, "error");
            return;
        }
        setErrors({});

        setLoading(true);
        try {
            const response = await authApi.login({ email: validatedEmail, password: trimmedPassword });
            // console.log("Login Response::", response);

            const { status, message, data, code } = response || {};
            const userType = data?.user_type;

            if (status === 0) {
                handleSnackbar("Network error. Please check your connection.", "warning");
                return;
            }

            if (status === 400 && code === "success" && data?.link) {
                handleSnackbar(message || "Redirecting...", "info");
                setTimeout(() => { window.location.href = data.link; }, 1000);
                return;
            }

            if (status === 429) {
                const retryAfter = data?.retry_after_seconds ?? 180;

                const lockUntil = Date.now() + retryAfter * 1000;

                setAuthData({
                    login_lock_until: {
                        ...login_lock_until,
                        [validatedEmail]: lockUntil
                    }
                });

                setLoginBlock({
                    isBlocked: true,
                    timeRemaining: retryAfter,
                });

                handleSnackbar(
                    "Too many failed login attempts. Please try again later.",
                    "error"
                );

                return;
            }

            if (status === 400 || status === 401) {
                handleSnackbar(
                    message || "Invalid email or password. Please check your credentials.",
                    "error"
                );
                return;
            }

            if (status !== 200) {
                handleSnackbar(message || "Something went wrong. Please try again.", "error");
                return;
            }

            if (status === 200 && code === "success") {
                // localStorage.setItem("user_type", userType);
                setAuthData({ user_type: userType });

                if (userType === "superadmin") {
                    setAuthData({ plan: data.plan || "free" });
                }

                setAuthData({ isAuthenticated: true });
                handleContextLogin();

                const routeMap = {
                    user: "/user/landing",
                    recruiter: "/recruiter/dashboard",
                    admin: "/admin/dashboard",
                    freelancer: "/recruiter/dashboard",
                    superadmin: "/tenant/management",
                };

                handleSnackbar(
                    message || "Login successful!",
                    "success"
                );

                navigate(routeMap[userType], {
                    replace: true, state: { "user_type": userType }
                });
            }
        } catch (err) {
            console.error("Unexpected login error:", err);
            handleSnackbar("Something went wrong. Please try again.", "error");
        } finally {
            setLoading(false);
        }
    };

    /* ---------------- UI ---------------- */

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                background: theme.palette.background.grad,
                p: 2,
                pb: 3,
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    width: "100%",
                    maxWidth: 1000,
                    borderRadius: 4,
                    overflow: "hidden",
                    boxShadow: theme.shadows[6],
                    background: theme.palette.background.paper,
                }}
            >
                {/* LEFT SIDE (LOGIN FORM) */}
                <Box
                    sx={{
                        flex: 1,
                        p: 4,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <form
                        onSubmit={handleLogin}
                        style={{
                            width: "100%",
                            maxWidth: 360
                        }}
                    >
                        <Box textAlign="center">
                            <Box
                                component="img"
                                src={Cybermeru}
                                alt="CyberMeru"
                                textAlign="center"
                                sx={{
                                    height: 70,
                                    mb: 1.5,
                                    mx: "auto",
                                }}
                            />
                            {/* <Typography
                                variant="h5"
                                sx={{
                                    fontWeight: 700,
                                    mb: 1,
                                    color: theme.palette.text.primary,
                                }}
                            >
                                Sign in to your workspace
                            </Typography> */}
                            {/* <Typography
                                variant="body2"
                                sx={{
                                    mb: 3,
                                    color: theme.palette.text.secondary,
                                    fontSize: "0.9rem",
                                }}
                            >
                                Sign in to continue
                            </Typography> */}
                        </Box>

                        {emailFromUrl && (
                            <Box sx={{ mb: 1, p: 1, borderRadius: 2, bgcolor: "action.hover", textAlign: "center" }}> {/* Tighter box padding/margin */}
                                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: "0.75rem" }}>
                                    Signing in as <span style={{ fontWeight: 700, color: theme.palette.text.primary }}>{emailFromUrl}</span>
                                </Typography>
                                <Link
                                    href={getCentralUrl("/email/redirect")}
                                    underline="hover"
                                    sx={{ display: "inline-block", fontSize: "0.7rem", color: theme.palette.primary.main, fontWeight: 600 }}
                                >
                                    Switch Account
                                </Link>
                            </Box>
                        )}

                        <CustomInput
                            label="Password"
                            name="password"
                            size="small"
                            required
                            type={showPassword ? "text" : "password"}
                            value={password}
                            onChange={handleChange}
                            error={!!errors.password}
                            helperText={errors.password || " "}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword((prev) => !prev)}
                                            edge="end"
                                            sx={{ color: theme.palette.primary.main }}
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <Box sx={{ textAlign: "right" }}>
                            <Link
                                component="button"
                                type="button"
                                underline="hover"
                                sx={{ fontSize: "0.75rem", color: theme.palette.primary.main, fontWeight: 600 }}
                                onClick={async () => {
                                    if (!emailFromUrl) {
                                        navigate("/forgot/password");
                                        return;
                                    }
                                    try {
                                        const response = await authApi.checkUserStatus({ email: emailFromUrl });
                                        const { status: httpStatus, data } = response || {};

                                        if (httpStatus !== 200) {
                                            navigate("/forgot/password");
                                            return;
                                        }

                                        if (data?.status === "inactive") {
                                            handleSnackbar(
                                                "Your account is not yet active. Please contact your administrator.",
                                                "warning"
                                            );
                                            return;
                                        }

                                        if (!data?.is_password_changed) {
                                            handleSnackbar(
                                                "You must complete your initial password setup first. Please use the link sent to your registration email.",
                                                "warning"
                                            );
                                            return;
                                        }

                                        navigate("/forgot/password");
                                    } catch {
                                        navigate("/forgot/password");
                                    }
                                }}
                            >
                                Forgot password?
                            </Link>
                        </Box>

                        {loginBlock.isBlocked && (
                            <Typography
                                variant="body2"
                                sx={{ mt: 1, mb: 1, color: theme.palette.error.main, fontWeight: 600, fontSize: "0.8rem" }}
                            >
                                Too many failed attempts. Try again in{" "}
                                {formatTime(loginBlock.timeRemaining)}
                            </Typography>
                        )}

                        <CustomButton
                            type="submit"
                            fullWidth
                            disabled={loading || loginBlock.isBlocked || password.trim().length < 13 || password.trim().length > 20 || Object.values(errors).some((v) => v)}
                            sx={{
                                height: 42,
                                mt: 1,
                                borderRadius: 3,
                                fontWeight: 700,
                                textTransform: "none",
                            }}
                        >
                            {loading ? <CircularProgress size={22} color="inherit" /> : "Continue"}
                        </CustomButton>

                        <Typography
                            variant="body2"
                            sx={{
                                textAlign: "center",
                                color: theme.palette.text.secondary,
                                fontSize: "0.75rem", // Slightly smaller
                                lineHeight: 1.5,
                            }}
                        >
                            Need help signing in? <b>Contact us at:</b> {" "}
                            <Link
                                href="mailto:support@merubot.com"
                                underline="hover"
                                sx={{ color: theme.palette.primary.main, fontWeight: 600 }}
                            >
                                support@merubot.com
                            </Link>
                        </Typography>
                    </form>
                </Box>

                {/* RIGHT SIDE (IMAGE) */}
                <Box
                    sx={{
                        flex: 1,
                        display: { xs: "none", md: "block" },
                        background: "#f5f7fb",
                    }}
                >
                    <Box
                        component="img"
                        src={LoginIllustration}
                        alt="AI Interview"
                        sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                </Box>
            </Box>

            <Typography
                component="footer"
                sx={{
                    mt: 3, // Reduced footer top margin
                    textAlign: "center",
                    fontSize: "0.7rem", // Smaller text
                    color: "rgba(35, 33, 33, 0.75)",
                    letterSpacing: 0.2,
                }}
            >
                © {new Date().getFullYear()} CyberMeru Technologies Pvt. Ltd. · All Rights Reserved.
            </Typography>

            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                autoHideDuration={6000}
            />
        </Box>
    );
};

export default Login;

